import React, { useEffect, useState } from 'react';
import { AxiosError } from 'axios';

import EngagementCard from 'components/wrapperEventManager/engagementCard.component';
import { PlusIcon } from '@common_components/icons/plus.icon';

import { EngagementService } from 'services/engagements.service';
import { ToastService } from 'services/toast.service';
import { useDispatch, useSelector } from 'react-redux';
import { User } from 'interfaces/user.interface';
import CellCardInteractionDrag from 'components/wrapperEventManager/cellCardInteractionDrag.component';
import { EventCommitment, Engagement, PutEventCommitment } from 'interfaces/types.interface';
import { dayjs, FORMAT_DATE_API, RootState, UUID, usePersistantState, CommonGlobalService } from '@umanforge/common';
import { KEY } from 'constants/local_storage_key.constant';
import { useTranslation } from 'react-i18next';
import { DEFAULT_BACKLOG } from 'constants/eventManager.constant';

import closedDayPng from '../../assets/closedDay.png';
import { EventManager } from 'interfaces/eventManager.interface';
import { StyleEventCard } from 'enum/styleEventCard.enum';

const toastService: ToastService = new ToastService( 'CellCard' );

interface CellCardProps {
  engagementsCell: EventCommitment[];
  dayCell: Date;
  userCell: ( User & { eventManagersId: UUID[]; } );
  dayOver: Date;
  eventManager: EventManager;
  userOver: User;
  draggedParticipantStart: User;
  draggedEngagement: EventCommitment | null;
  isDragging: boolean;
  isViewMode?: boolean;
  setIsDragging: ( dragging: boolean ) => void;
  getNextDayNotClosed: ( startDate: dayjs.Dayjs, userId: UUID ) => dayjs.Dayjs;
  dataEventManager: Map<string, Map<string, EventCommitment[]>>;
  fetchEngagementsOfEventManagerForDays: () => void;
  setDayOver: ( day: Date ) => void;
  setUserOver: ( user: User ) => void;
  onEngagementClick: ( ( task: EventCommitment ) => void ) | undefined;
  setIsPopupCreateEventVisible: ( visible: boolean ) => void;
  setDateClicked?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSelectedUserId?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setDraggedEngagement: ( engagementDrag: EventCommitment | null ) => void;
  setDraggedParticipantStart: ( participantStartDrag: User ) => void;
  isPopupCreateEventVisible: boolean;
  themeHightlighted?: string;
  isViewArchived?: boolean;
  cellClosed: boolean;
  canInteract?: boolean;
  isAdminOfEventManager?: boolean;
  isEngagementSharedAcrossMultipleTables?: ( engagement: EventCommitment ) => boolean;
  style: StyleEventCard;
}


const CellCard: React.FC<CellCardProps> = props => {
  const {
    userCell,
    isViewMode,
    themeHightlighted,
    dayCell,
    dayOver,
    cellClosed,
    isViewArchived,
    userOver,
    engagementsCell,
    eventManager,
    dataEventManager,
    draggedEngagement,
    draggedParticipantStart,
    isDragging,
    setIsDragging,
    setDraggedParticipantStart,
    setDraggedEngagement,
    setDayOver,
    setUserOver,
    getNextDayNotClosed,
    fetchEngagementsOfEventManagerForDays,
    onEngagementClick,
    setIsPopupCreateEventVisible,
    setSelectedUserId,
    style
  } = props;

  const { t } = useTranslation();
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  const [ selectedSectorId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SECTOR_ID, undefined );

  const [ dragMoveOrLinkEngagement, setDragMoveOrLinkEngagement ] = useState<number>( -1 );
  const [ duplicatedUsers, setDuplicatedUsers ] = useState<{ id: UUID, fullname: string }[]>( [] );
  const [ haveAllEventManagerOfEng, setHaveAllEventManagerOfEng ] = useState<boolean>( true );

  const isAdmin =  props.isAdminOfEventManager || ( me != undefined && selectedSectorId != undefined && CommonGlobalService.isAdminOfSector( me, selectedSectorId ) );
  const isOwner = (
    me && userCell &&
    ( me.id === userCell.id || userCell.id === DEFAULT_BACKLOG.ID )
  );
  const canInteract = typeof props.canInteract === 'boolean' ? props.canInteract : ( isAdmin || isOwner );

  useEffect( () => {
    if ( draggedEngagement ) {
      setHaveAllEventManagerOfEng( draggedEngagement.eventManagerIds.every( id => { return userCell.eventManagersId.includes( id ); } ) );
    }
  }, [ draggedEngagement ] );

  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const [ , setSelectedEventManagerRowUserId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_EVENT_MANAGER_ROW_USER_ID, undefined );
  const [ , setSelectedEventManagerId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_EVENT_MANAGER_ID, undefined );

  const findDuplicatedParticipantOnEngagement = ( newUserId: UUID, oldUserId: UUID, engagement: EventCommitment, newDay: dayjs.Dayjs ): { id: UUID, fullname: string }[] => {
    const engagementsOfEvent = [ ...dataEventManager.values() ]
      .map( map => {
        return [ ...map.values() ]
          .flat();
      } )
      .flat()
      .filter( engagementOfEventManager => { return engagementOfEventManager.eventId === engagement.eventId; } );

    const engagementsId = new Set();
    const uniqueEngagementsOfEvent = engagementsOfEvent.filter( ( { id } ) => { return !engagementsId.has( id ) && engagementsId.add( id ); } );
    const participantsId = new Set();
    const uniqueParticipantsOfEvent = uniqueEngagementsOfEvent.map( eng => { return eng.participants; } )
      .flat()
      .filter( ( { id } ) => { return !participantsId.has( id ) && participantsId.add( id ); } );

    const participantsMap: Record<string, number> = {};
    const duplicatedParticipants: { id: UUID, fullname: string }[] = [];
    uniqueEngagementsOfEvent.forEach( engagementOfEvent => {
      for ( const eventManagerIdOfEngagement of engagementOfEvent.eventManagerIds ) {
        for ( const participantOfEngagement of engagementOfEvent.participants ) {
          const participant = ( engagementOfEvent.id === engagement.id && participantOfEngagement.id === oldUserId ) ? uniqueParticipantsOfEvent.find( par => { return par.id === newUserId; } ) : participantOfEngagement;
          if ( participant ) {
            const date = engagementOfEvent.id === engagement.id ? newDay : ( engagementOfEvent.realDeliveryDate ?? engagementOfEvent.deliveryDate );
            const engagementKey = `${dayjs( date ).format( FORMAT_DATE_API )}_${eventManagerIdOfEngagement}_${participant.id}`;
            participantsMap[ engagementKey ] = ( participantsMap[ engagementKey ] || 0 ) + 1;
            if ( participantsMap[ engagementKey ] >= 2 ) {
              let fullname: string | null = null;
              if ( participant.id === DEFAULT_BACKLOG.ID ) {
                fullname = eventManager.backlogName;
              } else {
                fullname = participant.fullname ?? null;
                if ( !fullname ) {
                  fullname = [ participant.firstname, participant.lastname ].filter( item => { return item != undefined; } ).join( ' ' );
                }
              }
              duplicatedParticipants.push( { id: participant.id, fullname: fullname } );
            }
          }
        }
      }
    } );
    return duplicatedParticipants;
  };

  const isSameDragOver = ( participant: User, date: Date ): boolean => {
    return (
      userCell.id === participant.id &&
      dayCell.getTime() === date.getTime()
    );
  };

  /** Handles Drag Start */
  const handleDragStart = (
    e: React.DragEvent,
    engagement: EventCommitment,
    fromDay: Date,
    participantStart: User,
  ) => {
    setDraggedEngagement( engagement );
    setDraggedParticipantStart( participantStart );
    e.dataTransfer.setData( 'participantStart', JSON.stringify( participantStart ) );
    e.dataTransfer.setData( 'engagement', JSON.stringify( engagement ) );
    e.dataTransfer.setData( 'fromDay', JSON.stringify( fromDay ) );
  };

  //----------- Reset on when drop on disabled cell ---------------\\
  const handleResetOnDropDisable = () => {
    setDayOver( dayjs().toDate() );
    setDraggedEngagement( null );
    setUserOver( { id: '0' } );
  };

  //----------- Move Engagement to new Date and user ---------------\\
  const handleDropToMoveEngagement = async ( e: React.DragEvent, toDay: Date ) => {
    setDayOver( dayjs().toDate() );
    setDraggedEngagement( null );
    setUserOver( { id: '0' } );
    try {
      const engagementDrag = JSON.parse( e.dataTransfer.getData( 'engagement' ) );

      const participantStart = JSON.parse( e.dataTransfer.getData( 'participantStart' ) ) as User;
      const currentDate = dayjs( engagementDrag.deliveryDate ).startOf( 'day' );
      const newDate = getNextDayNotClosed( dayjs( toDay ).startOf( 'day' ), userCell.id );
      if ( !( participantStart.id == userCell.id && currentDate.isSame( newDate ) ) ) {
        const body: PutEventCommitment = {};
        body.deliveryDate = newDate.format( FORMAT_DATE_API );
        body.participants = ( engagementDrag.participants as User[] )
          .filter( ( p: { id: UUID } ) => {return p.id !== participantStart.id;} )
          .map( user => { return user.id; } );
        body.participants.push( userCell.id );

        const isUpdate = await EngagementService.updateEngagementAPI( engagementDrag.id, body, token, dispatch );

        setDragMoveOrLinkEngagement( -1 );
        if ( isUpdate ) {
          toastService.showSuccess( t( 'translation:commitment_move' ) );
          fetchEngagementsOfEventManagerForDays();
        }
      }
    } catch ( error ) {
      const errorMessage = ( error as AxiosError ).response?.data || t( 'translation:update_error' );
      toastService.showError( errorMessage as string );
    }
  };

  //----------- Add new Engagement to new Date and user ---------------\\
  const handleDropToAddEngagementToUser = async ( e: React.DragEvent, toDay: Date ) => {
    setDraggedEngagement( null );
    setDayOver( dayjs().toDate() );
    setUserOver( { id: '' } );
    try {
      const engagementData = e.dataTransfer.getData( 'engagement' );
      const engagementDrag = JSON.parse( engagementData ) as EventCommitment;

      const engagementToCreate: Engagement = {
        eventId: engagementDrag.eventId,
        eventManagerIds: [ eventManager.id ],
        deliveryDate: getNextDayNotClosed( dayjs( toDay ).startOf( 'day' ), userCell.id ).format( FORMAT_DATE_API ),
        participants: [ userCell.id ],
      };

      const engagementId = await EngagementService.createEngagement( engagementToCreate, token, dispatch );
      setDragMoveOrLinkEngagement( -1 );
      if ( engagementId != null && engagementId != '' ) {
        toastService.showSuccess( t( 'translation:commitment_share' ) );
        fetchEngagementsOfEventManagerForDays();
      }
    } catch ( error ) {
      const errorMessage = ( error as AxiosError ).response?.data || t( 'translation:update_error' );
      toastService.showError( errorMessage as string );
    }
  };

  /** Handles Drag Over */
  const handleDragOver = ( e: React.DragEvent, participantDragOver: User, day: Date ) => {
    if ( draggedEngagement ) {
      setDayOver( day );
      setUserOver( participantDragOver );
      setDuplicatedUsers( findDuplicatedParticipantOnEngagement( userCell.id, draggedParticipantStart.id, draggedEngagement, dayjs( day ) ) );
    }
    e.preventDefault();
  };

  const handleDragLeave = ( ) => {
    setDuplicatedUsers( [] );
    setDayOver( dayjs().toDate() );
    setUserOver( { id: '' } );
  };


  const engagementAnglesRef = React.useRef<{ [ id: string ]: number }>( {} );
  function getEngagementAngle( id?: string ): number {
    if ( !id ) { return 0; }
    if ( !( id in engagementAnglesRef.current ) ) {
      engagementAnglesRef.current[ id ] = parseFloat( ( Math.random() * 10 - 5 ).toFixed( 1 ) );
    }
    return engagementAnglesRef.current[ id ];
  }

  if ( cellClosed && engagementsCell.length === 0 ) {
    return (
      <div className="p-1.5">
        <div
          className="min-w-48 bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9] shadow-md h-full rounded-xl relative overflow-hidden flex items-center justify-center border-2 border-dashed border-rose-200"
          style={{ backgroundImage: `url(${ closedDayPng })`, backgroundSize: 73 }}
        >
        </div>
      </div>
    );
  }

  const cellBackgroundStyle = cellClosed && engagementsCell.length > 0 ? {
    backgroundImage: `url(${ closedDayPng })`,
    backgroundSize: 73,
    backgroundRepeat: 'repeat',
    backgroundPosition: 'center'
  } : {};

  return (
    <div className={`${( cellClosed && engagementsCell.length > 0 ) ? 'p-1.5' : 'p-[3px]'}`}>
      <div
        className={`
          relative flex flex-wrap h-full min-w-48 w-full p-2 gap-4 items-center justify-center rounded-xl shadow-md group trans-fast
            ${ isSameDragOver( userOver, dayOver ) ? ( cellClosed && engagementsCell.length > 0 ? 'bg-gray-100' : 'bg-white-base' ) : ( cellClosed && engagementsCell.length > 0 ? 'bg-gray-100' : 'bg-white' ) }
          ${( !canInteract || isViewArchived || isViewMode || ( cellClosed && engagementsCell.length > 0 ) ) ? 'cursor-default' : 'cursor-pointer'}
          ${( cellClosed && engagementsCell.length > 0 ) ? 'bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9] border-2 border-dashed border-rose-200 overflow-hidden' : ''}
        `}
        style={cellBackgroundStyle}
        onDragOver={( isViewArchived || isViewMode || ( cellClosed && engagementsCell.length > 0 ) ) ? undefined : e => {return handleDragOver( e, userCell, dayCell );}}
        onDragLeave={( isViewArchived || isViewMode || ( cellClosed && engagementsCell.length > 0 ) ) ? undefined : () => { setDuplicatedUsers( [] ); handleDragLeave(); }}
        onDrop={( isViewArchived || isViewMode || ( cellClosed && engagementsCell.length > 0 ) ) ? undefined : () => {return setDuplicatedUsers( [] );}}
        onClick={ ( !canInteract || isViewArchived || isViewMode || ( cellClosed && engagementsCell.length > 0 ) ) ? undefined : () => {
          if ( typeof props.setDateClicked === 'function' ) {
            props.setDateClicked( dayjs( dayCell ).format( FORMAT_DATE_API ) );
          }
          setSelectedEventManagerRowUserId( userCell.id );
          setSelectedEventManagerId( eventManager.id );
          if ( typeof setSelectedUserId === 'function' ) {
            setSelectedUserId( userCell.id );
          }
          setIsPopupCreateEventVisible( true );
        }}
      >
        {/* Interaction On Engagement Drag Hover */}
        {canInteract && !( isViewArchived || isViewMode ) && !( cellClosed && engagementsCell.length > 0 ) && (
          <>
            <CellCardInteractionDrag
              handleResetOnDropDisable={handleResetOnDropDisable}
              isDragging={isDragging}
              duplicatedUsers={duplicatedUsers}
              dayCell={dayCell}
              dayOver={dayOver}
              draggedParticipantStart={draggedParticipantStart}
              dragMoveOrLinkEngagement={dragMoveOrLinkEngagement}
              draggedEngagement={draggedEngagement}
              handleDropToAddEngagementToUser={handleDropToAddEngagementToUser}
              handleDropToMoveEngagement={handleDropToMoveEngagement}
              isSameDragOver={isSameDragOver}
              setDragMoveOrLinkEngagement={setDragMoveOrLinkEngagement}
              userCell={userCell}
              userOver={userOver}
              canMove={haveAllEventManagerOfEng}
            />

            <div className={`absolute inset-0 z-0 rounded-xl trans-fast p-4 border-2 flex-center group-hover:scale-100 group-hover:border-orange-base
              ${isSameDragOver( userOver, dayOver ) ? 'scale-100 border-orange-base' : 'bg-transparent border-transparent scale-90'}
            `}>
              {engagementsCell.length == 0 && (
                <div className='group-hover:translate-y-0 text-orange-base gap-3 flex-center flex-col trans-fast translate-y-5 opacity-0 group-hover:opacity-100'>
                  <div className="p-3 bg-[#ffe1d8] rounded-md shadow-md">
                    <PlusIcon size={7} />
                  </div>
                  <p>{ t( 'translation:create_event' ) }</p>
                </div>
              )}
            </div>
          </>
        )}
        {/* Engagements */}
        {engagementsCell
          .map( ( engagement, engagementIndex ) => {
            const isOnEng = (
              me &&
              engagement.participants.some( participant => { return participant.id === me.id; } )
            );
            const isOnlyBacklogOnEng = (
              engagement.participants.length === 1 &&
              engagement.participants.some( participant => { return participant.id === DEFAULT_BACKLOG.ID; } )
            );

            return (
              <EngagementCard
                key={`Engagement-${userCell.id}-${dayCell}-${engagementIndex}`}
                user={userCell}
                isDragging={isDragging}
                handleDragStart={handleDragStart}
                themeHightlighted={themeHightlighted}
                cursor={ onEngagementClick ? 'cursor-pointer' : '' }
                setIsDragging={setIsDragging}
                rotation={getEngagementAngle( engagement.id )}
                engagement={engagement}
                isHighlighted={Boolean( engagement.highlighted )}
                canDrag={!( isViewArchived || isViewMode || !canInteract || !( isAdmin || isOnEng || isOnlyBacklogOnEng ) || engagement.archived )}
                engagementIndex={engagementIndex}
                onEngagementClick={onEngagementClick}
                dayStart={dayCell}
                style={ style }
              />
            );
          } )}
      </div>
    </div>
  );
};

export default CellCard;
